import * as React from "react";
import { observer } from "mobx-react";
import { UitkImage, UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { UitkCard, UitkCardMedia, UitkCardLink, UitkCardTextSegment } from "uitk-react-cards";

import { DestinationSuggestionsProps } from "../typings";
import { Destination } from "src/typings/microserviceModels/destination-suggestions-flex-module.d.ts";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useLocalization } from "@shared-ui/localization-context";
import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
} from "src/components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext.tsx";
import { Heading } from "../Heading/Heading";

const computeDistance = (driveDistanceinMeters: string) => {
  if (!driveDistanceinMeters || driveDistanceinMeters === "0" || driveDistanceinMeters === "-1") {
    return "";
  }
  const distInKm = Math.round(parseInt(driveDistanceinMeters, 10) / 1000);
  const hours = Math.floor(distInKm / 100);
  const minutes = Math.round((distInKm % 100) * 0.6);
  const { formatText } = useLocalization();

  if (hours === 0) {
    return formatText("destinationSuggestions.distance.minutesAway", minutes);
  }
  if (hours === 1) {
    if (minutes < 2) {
      return formatText("destinationSuggestions.distance.oneHourAway");
    }

    return formatText("destinationSuggestions.distance.oneHourAndMinutesAway", minutes);
  }
  if (hours <= 7) {
    if (minutes < 2) {
      return formatText("destinationSuggestions.distance.hoursAway", hours);
    }

    return formatText("destinationSuggestions.distance.hoursAndMinutesAway", hours, minutes);
  }

  return null;
};

const WeekendGetawaysCards = withMabTracker(
  ({
    destination,
    displayDescription,
    displayCost,
    displayWeather,
    displayDistance,
    cardIndex,
    mabTrack,
  }: {
    destination: Destination;
    displayDescription: boolean;
    displayCost: boolean;
    displayWeather: boolean;
    displayDistance: boolean;
    cardIndex: number;
    mabTrack?: (x: MabTrackEvent) => null;
  }) => {
    const avgTime = displayDistance && computeDistance(destination.driveDistanceinMeters);
    const rfrrId = cardIndex + 1;
    const { formatText } = useLocalization();

    const cardClicked = mabTrack
      ? () => {
          const mabTrackData: MabTrackEvent = {
            moduleName: "Weekend_getaways",
            eventTarget: "Weekend_getaways",
            eventType: "homepageClick.path",
            rfrr: rfrrId.toString(),
          };
          mabTrack(mabTrackData);
        }
      : undefined;

    const backgroundColor = React.useContext(PageBackgroundContext);
    const withBorder = backgroundColor === BackgroundColorTokens.BACKGROUND_WHITE;

    return (
      <UitkCard className="uitk-flex-justify-content-flex-start uitk-flex-column" withBorder={withBorder}>
        <UitkCardMedia>
          <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
            <UitkImage src={destination.destinationImg} alt={destination.imgAlt} />
            <UitkScrim type={UitkScrimType.BOTTOM}>
              <h3 className="uitk-type-heading-500 cardHeading">
                {destination.name}
                {destination.destinationGeo.inPos &&
                  destination.destinationGeo.state &&
                  `, ${destination.destinationGeo.state}`}
                {!destination.destinationGeo.inPos &&
                  destination.destinationGeo.country &&
                  `, ${destination.destinationGeo.country}`}
              </h3>
              {displayWeather && destination.destinationWeather && destination.destinationWeather.avgTemp && (
                <h4 className="uitk-type-paragraph-300 cardHeading">
                  {destination.destinationWeather.unit === "°F"
                    ? formatText("destinationSuggestions.weatherInformation.F", destination.destinationWeather.avgTemp)
                    : formatText("destinationSuggestions.weatherInformation.C", destination.destinationWeather.avgTemp)}
                </h4>
              )}
            </UitkScrim>
          </UitkFigure>
          <UitkCardLink>
            <TrackedLink
              moduleName="DestinationSuggestions"
              action={Action.CLICK}
              href={destination.url}
              rfrr={rfrrId}
              onClick={cardClicked}
            >
              {destination.name}
            </TrackedLink>
          </UitkCardLink>
        </UitkCardMedia>
        {(displayDescription || displayCost) && (
          <div className="uitk-card-content uitk-cell">
            <UitkCardTextSegment className="uitk-type-paragraph-300">
              {displayDescription && <div>{destination.destinationDescriptions.SHORT}</div>}
              {displayCost && destination.averageLodgingCost && (
                <div className="uitk-type-bold all-t-padding-two">
                  {formatText("destinationSuggestions.averageCost", destination.averageLodgingCost)}
                </div>
              )}
              {displayDistance && avgTime && (
                <div className="all-t-padding-two">
                  <UitkIcon className="distance-icon" size={UitkIconSize.SMALL} name="directions_car" />
                  <span className="all-l-padding-one">{avgTime}</span>
                </div>
              )}
            </UitkCardTextSegment>
          </div>
        )}
      </UitkCard>
    );
  }
);

export const WeekendGetaways = observer(({ props }: { props: DestinationSuggestionsProps }) => {
  const keyHelper = new ItemKeyHelper(props.flexComponent.id);
  const {
    month,
    origin,
    destinations,
    disableOriginSelector,
    displayLodgingCost,
    displayWeather,
    displayDescriptions,
    displayDistanceTime,
  } = props.flexComponent.model;
  if (destinations.length < 4) {
    return null;
  }

  // Display description only if all the cards have description in the required format.
  const displayDescription =
    displayDescriptions &&
    destinations.filter(
      (destination) => destination.destinationDescriptions && destination.destinationDescriptions.SHORT
    ).length === destinations.length;

  const { formatText } = useLocalization();

  return (
    <div
      data-testid="weekend-getaways"
      className="DestinationSuggestions uitk-cell all-b-margin-three"
      id={props.flexComponent.id}
      data-fm={props.flexComponent.fmId}
    >
      <Heading month={month} origin={origin} disableOriginSelector={disableOriginSelector} />
      <IsomorphicCarousel
        className="carousel-component"
        itemsVisible={{
          lg: 3,
          md: 2,
          sm: 1,
        }}
        buttonText={{
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev"),
        }}
        pageBy={1}
        peekWidth={{
          lg: 0,
          md: 24,
          sm: 24,
        }}
      >
        {destinations.map((destination, index) => {
          return (
            <div className="uitk-carousel-item uitk-cell" key={keyHelper.next()}>
              <WeekendGetawaysCards
                destination={destination}
                displayDescription={displayDescription}
                displayCost={displayLodgingCost}
                displayWeather={displayWeather}
                displayDistance={displayDistanceTime}
                cardIndex={index}
                mabTrack={props.mabTrack}
              />
            </div>
          );
        })}
      </IsomorphicCarousel>
    </div>
  );
});
(WeekendGetaways as React.SFC).displayName = "WeekendGetawaysView";
